import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { Repositories } from '@/infra/repositories';
import { type Response as CartSummaryResponse } from '@/infra/services/checkout/cart/get-cart-summary';
import { useGlobalStore } from '@/presenters/store/global';

type QueryOptions<TData = CartSummaryResponse> = Omit<
  UseQueryOptions<CartSummaryResponse, Error, TData>,
  'queryKey' | 'queryFn'
>;

export function useCartSummary<TData = CartSummaryResponse>(
  options?: QueryOptions<TData>
) {
  const cartId = useGlobalStore((store) => store.session?.cartId);

  const query = useQuery<CartSummaryResponse, Error, TData>({
    queryKey: ['cart-summary', cartId],
    queryFn: async () => {
      if (!cartId) {
        throw new Error('Cart ID not found');
      }
      return await Repositories.Checkout.Cart.getCartSummary({ cartId });
    },
    enabled: !!cartId,
    staleTime: 1000 * 60 * 5,
    retry: 2,
    ...options,
  });

  return {
    ...query,
    isLoading: query.isLoading,
    error: query.error,
  };
}
